import React, { memo, useCallback, useContext, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { FieldSet } from '@zillow/constellation-10';

import { debounce } from '@zg-rentals/zdash';

import PropertiesActions from '../../../../actions/PropertiesActions';
import actions from '../../../../containers/CreateListingPage/actions';
import { useGuardedMultiEdit } from '../../../../hooks';
import { multiFieldsUtil, SAVE_TO_PARENT_KEY } from '../../../../utils/postingUtils';
import wizardActions from '../../../Wizard/actions';

import { roommatesOptionsArray } from '../../../../constants/ListingDetailsConstants';
import TimeConstants from '../../../../constants/TimeConstants';
import { DISPLAY_SERVER_ERROR_AT } from '../../../../constants/WizardConstants';
import WizardServerError from '../../../../model/WizardServerError';

import { ModularListingContext } from '../../../../containers/CreateListingPage/context';

import { WizardFormSectionContainer, WizardHeading } from '../../../../styles/wizard';

import { WizardContext } from '../../../Wizard/context';
import DropdownFormField from '../../FormFields/DropdownFormField';

const propTypes = {};

const defaultProps = {};

// the fields that need to be saved successfully before we close the edit modal
const SUCCESS_FIELDS = [
  'roommateFemaleCount',
  'roommateMaleCount',
  'roommateNonBinaryCount',
  'roommatePreferNotToIdentifyCount',
];

const DetailedRoommateCountFormSectionContainer = () => {
  const dispatch = useDispatch();

  const {
    reducerWizardDispatch,
    wizardState: { isFetchingData },
  } = useContext(WizardContext);

  const {
    modularListingState: { dataToBeSavedMap, isInModalState, serverErrorObj },
    reducerDispatch,
  } = useContext(ModularListingContext);

  const {
    listingAlias,
    roomForRent: {
      currentRoommatesDetails: {
        roommateFemaleCount = null,
        roommateMaleCount = null,
        roommateNonBinaryCount = null,
        roommatePreferNotToIdentifyCount = null,
      },
    } = {},
  } = useSelector((state) => state.properties.currentProperty);

  const { saveMultipleFieldsPropertyInfo } = useGuardedMultiEdit();

  useEffect(() => {
    actions.clearFieldDataEntry({ reducerDispatch });

    return () => {
      actions.clearFieldDataEntry({ reducerDispatch });
    };
  }, []);

  useEffect(() => {
    if (isInModalState) {
      setSaveOutsideComponent(true);
    }

    return () => {
      setSaveOutsideComponent(false);
    };
  }, []);

  useEffect(() => {
    if (isFetchingData || hasServerError()) {
      return;
    }

    if (checkIfAllFieldsHaveValues()) {
      saveDebounced();
    }
  }, [dataToBeSavedMap.values()]);

  const checkIfAllFieldsHaveValues = () => {
    const keyIntersections = getArrayIntersection(SUCCESS_FIELDS, Array.from(dataToBeSavedMap.keys()));

    const response = SUCCESS_FIELDS.length === keyIntersections.length;

    return response;
  };

  const getArrayIntersection = (array1, array2) => {
    return array1.filter((element) => array2.includes(element));
  };

  const hasServerError = () => {
    return serverErrorObj.fieldName !== '';
  };

  const getDataToSave = () => {
    let data = {};

    for (const valueObj of dataToBeSavedMap.values()) {
      const { fieldName, value } = valueObj;

      data = {
        ...data,
        [fieldName]: value === '' ? null : value,
      };
    }

    const response = {
      property: {
        listingAlias,
        roomForRent: {
          ...data,
        },
      },
    };

    // sometimes data to save is empty
    // need a better way to check real value changes at useEffect dependency dataToBeSavedMap.values
    if (Object.keys(response.property.roomForRent).length === 0) {
      return false;
    }

    return response;
  };

  const save = () => {
    const dataToSave = getDataToSave();

    if (dataToSave === false) {
      return;
    }

    wizardActions.updateIsFetchingData({ reducerWizardDispatch, isFetchingData: true });

    return saveMultipleFieldsPropertyInfo({ postData: dataToSave })
      .then((response) => {
        const errorMessage = multiFieldsUtil.getMultiEditErrorMessageFromResponse(response);
        if (errorMessage) {
          updateServerError(errorMessage, 'multi');
          return;
        }
        dispatch(
          PropertiesActions.updateCurrentPropertyCurrentRoommatesDetails({
            ...dataToSave.property.roomForRent,
          }),
        );

        actions.clearFieldDataEntry({ reducerDispatch });
        actions.updateIsSaveComplete({ reducerDispatch, isSaveComplete: false });
      })
      .catch((error) => {
        /**
         * TODO: handle errors for review edit modal
         *
         * need to refactor all implementations of saveMultipleFieldsPropertyInfo
         * and handle errors based on fieldName
         *
         * use 'multi' for now
         */
        updateServerError(error?.message ?? '', 'multi');
      })
      .finally(() => {
        wizardActions.updateIsFetchingData({
          reducerWizardDispatch,
          isFetchingData: false,
        });
      });
  };

  const saveDebounced = useCallback(debounce(save, TimeConstants.SELECTION_DEBOUNCE_TIME_MS), []);

  const setSaveOutsideComponent = (saveOutsideComponent = true) => {
    actions.updateAnyProperty({
      reducerDispatch,
      data: {
        saveOutsideComponent,
      },
    });
  };

  const updateServerError = (message = '', fieldName = '') => {
    const displayAt = isInModalState ? DISPLAY_SERVER_ERROR_AT.FIELD : DISPLAY_SERVER_ERROR_AT.PAGE;

    actions.updateServerError({
      reducerDispatch,
      serverErrorObj: WizardServerError({
        displayAt,
        fieldName,
        message,
        displayMessage: message,
      }),
    });
  };

  return (
    <WizardFormSectionContainer data-test="TotalRoommateCountFormSectionContainer">
      <WizardHeading level={1} textStyle="heading-sm">
        How many roommates will the renter have?
      </WizardHeading>

      <FieldSet>
        <DropdownFormField
          inputName="roommateMaleCount"
          initialValue={roommateMaleCount}
          saveToParentKey={SAVE_TO_PARENT_KEY.ROOM_FOR_RENT}
          label="Male"
          optionsList={roommatesOptionsArray}
        />
        <DropdownFormField
          inputName="roommateFemaleCount"
          initialValue={roommateFemaleCount}
          saveToParentKey={SAVE_TO_PARENT_KEY.ROOM_FOR_RENT}
          label="Female"
          optionsList={roommatesOptionsArray}
        />
        <DropdownFormField
          inputName="roommateNonBinaryCount"
          initialValue={roommateNonBinaryCount}
          saveToParentKey={SAVE_TO_PARENT_KEY.ROOM_FOR_RENT}
          label="Non-binary"
          optionsList={roommatesOptionsArray}
        />
        <DropdownFormField
          inputName="roommatePreferNotToIdentifyCount"
          initialValue={roommatePreferNotToIdentifyCount}
          saveToParentKey={SAVE_TO_PARENT_KEY.ROOM_FOR_RENT}
          label="Prefer not to identify"
          optionsList={roommatesOptionsArray}
        />
      </FieldSet>
    </WizardFormSectionContainer>
  );
};

DetailedRoommateCountFormSectionContainer.propTypes = propTypes;
DetailedRoommateCountFormSectionContainer.defaultProps = defaultProps;

export default memo(DetailedRoommateCountFormSectionContainer);
